import React, { useState, useEffect, useContext } from "react";
import {
  makeStyles,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  IconButton,
  TextField,
  MenuItem,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Box,
  CircularProgress,
  Select,
  FormControl,
  InputLabel
} from "@material-ui/core";
import {
  Visibility,
  Send,
  FilterList
} from "@material-ui/icons";
import { toast } from "react-toastify";
import { format, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";

import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import MainContainer from "../../components/MainContainer";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: "auto",
  },
  filtersBox: {
    display: "flex",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    flexWrap: "wrap",
  },
  chatContainer: {
    display: "flex",
    flexDirection: "column",
    height: "50vh",
    overflowY: "auto",
    padding: theme.spacing(2),
    background: "#f5f5f5",
    borderRadius: 8,
    marginBottom: theme.spacing(2),
  },
  messageBubble: {
    maxWidth: "70%",
    padding: theme.spacing(1.5),
    borderRadius: 12,
    marginBottom: theme.spacing(1),
  },
  clientMessage: {
    alignSelf: "flex-start",
    background: "#fff",
    border: "1px solid #e0e0e0",
  },
  adminMessage: {
    alignSelf: "flex-end",
    background: "#dcf8c6",
  },
  messageInput: {
    display: "flex",
    gap: theme.spacing(1),
  },
  urgentRow: {
    background: "#fff5f5",
  },
}));

const priorityLabels = { low: "Baixa", medium: "Média", high: "Alta", urgent: "Urgente" };
const statusLabels = { open: "Aberto", in_progress: "Em Andamento", resolved: "Resolvido", closed: "Fechado" };
const statusColors = { open: "#4caf50", in_progress: "#2196f3", resolved: "#ff9800", closed: "#9e9e9e" };
const categoryLabels = { bug: "Bug", question: "Dúvida", suggestion: "Sugestão", financial: "Financeiro", technical: "Técnico" };

const AdminSupportTickets = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);

  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDetailModal, setOpenDetailModal] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [newMessage, setNewMessage] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterPriority, setFilterPriority] = useState("");

  useEffect(() => {
    loadTickets();
  }, [filterStatus, filterPriority]);

  const loadTickets = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterStatus) params.status = filterStatus;
      if (filterPriority) params.priority = filterPriority;
      const { data } = await api.get("/admin/support-tickets", { params });
      setTickets(data.tickets);
    } catch (err) {
      toast.error("Erro ao carregar tickets");
    }
    setLoading(false);
  };

  const handleOpenDetail = async (ticketId) => {
    try {
      const { data } = await api.get(`/admin/support-tickets/${ticketId}`);
      setSelectedTicket(data);
      setOpenDetailModal(true);
    } catch (err) {
      toast.error("Erro ao carregar ticket");
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;
    try {
      await api.post(`/admin/support-tickets/${selectedTicket.id}/messages`, { body: newMessage });
      setNewMessage("");
      const { data } = await api.get(`/admin/support-tickets/${selectedTicket.id}`);
      setSelectedTicket(data);
    } catch (err) {
      toast.error("Erro ao enviar mensagem");
    }
  };

  const handleUpdateTicket = async (field, value) => {
    try {
      const { data } = await api.put(`/admin/support-tickets/${selectedTicket.id}`, { [field]: value });
      setSelectedTicket({ ...selectedTicket, ...data });
      loadTickets();
      toast.success("Ticket atualizado");
    } catch (err) {
      toast.error("Erro ao atualizar ticket");
    }
  };

  const handleCloseTicket = async () => {
    try {
      await api.put(`/admin/support-tickets/${selectedTicket.id}/close`);
      toast.success("Ticket fechado");
      setOpenDetailModal(false);
      loadTickets();
    } catch (err) {
      toast.error("Erro ao fechar ticket");
    }
  };

  return (
    <MainContainer>
      <MainHeader>
        <Title>Tickets de Suporte</Title>
      </MainHeader>

      <Paper className={classes.mainPaper} variant="outlined">
        <Box className={classes.filtersBox}>
          <TextField
            select
            label="Status"
            variant="outlined"
            size="small"
            style={{ minWidth: 150 }}
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <MenuItem value="">Todos</MenuItem>
            <MenuItem value="open">Aberto</MenuItem>
            <MenuItem value="in_progress">Em Andamento</MenuItem>
            <MenuItem value="resolved">Resolvido</MenuItem>
            <MenuItem value="closed">Fechado</MenuItem>
          </TextField>
          <TextField
            select
            label="Prioridade"
            variant="outlined"
            size="small"
            style={{ minWidth: 150 }}
            value={filterPriority}
            onChange={(e) => setFilterPriority(e.target.value)}
          >
            <MenuItem value="">Todas</MenuItem>
            <MenuItem value="low">Baixa</MenuItem>
            <MenuItem value="medium">Média</MenuItem>
            <MenuItem value="high">Alta</MenuItem>
            <MenuItem value="urgent">Urgente</MenuItem>
          </TextField>
        </Box>

        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : tickets.length === 0 ? (
          <Box display="flex" justifyContent="center" p={4}>
            <Typography color="textSecondary">Nenhum ticket encontrado.</Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Assunto</TableCell>
                  <TableCell>Empresa</TableCell>
                  <TableCell>Categoria</TableCell>
                  <TableCell>Prioridade</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Responsável</TableCell>
                  <TableCell>Data</TableCell>
                  <TableCell>Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tickets.map((ticket) => (
                  <TableRow
                    key={ticket.id}
                    className={ticket.priority === "urgent" ? classes.urgentRow : ""}
                  >
                    <TableCell>#{ticket.id}</TableCell>
                    <TableCell>{ticket.subject}</TableCell>
                    <TableCell>{ticket.company?.name || "-"}</TableCell>
                    <TableCell>{categoryLabels[ticket.category] || ticket.category}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={priorityLabels[ticket.priority]}
                        style={ticket.priority === "urgent" ? { background: "#f44336", color: "#fff" } : {}}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={statusLabels[ticket.status]}
                        style={{ background: statusColors[ticket.status], color: "#fff" }}
                      />
                    </TableCell>
                    <TableCell>{ticket.assignedUser?.name || "-"}</TableCell>
                    <TableCell>
                      {format(parseISO(ticket.createdAt), "dd/MM/yyyy HH:mm", { locale: ptBR })}
                    </TableCell>
                    <TableCell>
                      <IconButton size="small" onClick={() => handleOpenDetail(ticket.id)}>
                        <Visibility />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Modal Detalhe */}
      <Dialog open={openDetailModal} onClose={() => setOpenDetailModal(false)} maxWidth="md" fullWidth>
        {selectedTicket && (
          <>
            <DialogTitle>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <span>#{selectedTicket.id} - {selectedTicket.subject}</span>
                <Chip
                  size="small"
                  label={statusLabels[selectedTicket.status]}
                  style={{ background: statusColors[selectedTicket.status], color: "#fff" }}
                />
              </Box>
            </DialogTitle>
            <DialogContent>
              <Grid container spacing={2} style={{ marginBottom: 16 }}>
                <Grid item xs={4}>
                  <TextField
                    fullWidth
                    select
                    label="Status"
                    variant="outlined"
                    size="small"
                    value={selectedTicket.status}
                    onChange={(e) => handleUpdateTicket("status", e.target.value)}
                  >
                    <MenuItem value="open">Aberto</MenuItem>
                    <MenuItem value="in_progress">Em Andamento</MenuItem>
                    <MenuItem value="resolved">Resolvido</MenuItem>
                    <MenuItem value="closed">Fechado</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    fullWidth
                    select
                    label="Prioridade"
                    variant="outlined"
                    size="small"
                    value={selectedTicket.priority}
                    onChange={(e) => handleUpdateTicket("priority", e.target.value)}
                  >
                    <MenuItem value="low">Baixa</MenuItem>
                    <MenuItem value="medium">Média</MenuItem>
                    <MenuItem value="high">Alta</MenuItem>
                    <MenuItem value="urgent">Urgente</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="caption" color="textSecondary">
                    Empresa: {selectedTicket.company?.name || "-"}<br />
                    Aberto por: {selectedTicket.user?.name || "-"}<br />
                    Categoria: {categoryLabels[selectedTicket.category]}
                  </Typography>
                </Grid>
              </Grid>

              <div className={classes.chatContainer}>
                {selectedTicket.messages?.map((msg) => (
                  <div
                    key={msg.id}
                    className={`${classes.messageBubble} ${msg.isFromAdmin ? classes.adminMessage : classes.clientMessage}`}
                  >
                    <Typography variant="caption" color="textSecondary">
                      {msg.user?.name} {msg.isFromAdmin ? "(Admin)" : "(Cliente)"} - {format(parseISO(msg.createdAt), "dd/MM HH:mm")}
                    </Typography>
                    <Typography variant="body2">{msg.body}</Typography>
                  </div>
                ))}
              </div>

              {selectedTicket.status !== "closed" && (
                <div className={classes.messageInput}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    size="small"
                    placeholder="Responder ao cliente..."
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyPress={(e) => e.key === "Enter" && handleSendMessage()}
                  />
                  <IconButton color="primary" onClick={handleSendMessage}>
                    <Send />
                  </IconButton>
                </div>
              )}
            </DialogContent>
            <DialogActions>
              {selectedTicket.status !== "closed" && (
                <Button color="secondary" onClick={handleCloseTicket}>
                  Finalizar Ticket
                </Button>
              )}
              <Button onClick={() => setOpenDetailModal(false)}>Fechar</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </MainContainer>
  );
};

export default AdminSupportTickets;
